import type { ConformityV2Framework } from "../../../api/conformityApi"
import { FrameworkResultRow } from "./FrameworkResultRow"

/** The evaluation pane's framework list. Order comes from the backend
 *  (FRAMEWORK_ORDER). Everything is lifted to the editor, including which
 *  rows the user opened or closed, so a re-evaluate (which re-renders this
 *  pane) does not reset the rows to their default expansion. */
export function FrameworkResultList({
  frameworks,
  readOnly,
  canOverride,
  canEditProducts,
  onToggleInclude,
  onHover,
  onJumpToBlock,
  onOverride,
  onClearOverride,
  onAddScip,
  overrideByName,
  expanded,
  onToggleExpand,
}: {
  frameworks: ConformityV2Framework[]
  /** Rows the user explicitly opened (true) or closed (false). Every row
   *  starts open; only a deliberate collapse closes it. */
  expanded: Record<string, boolean>
  onToggleExpand: (frameworkKey: string, next: boolean) => void
  readOnly: boolean
  canOverride: boolean
  /** MC_MASTER_DATA_EDIT — gates the own-SCIP link (see FrameworkResultRow). */
  canEditProducts: boolean
  onToggleInclude: (frameworkKey: string, selected: boolean) => void
  onHover: (key: string | null) => void
  /** Click a row → scroll its block into view in the document pane. */
  onJumpToBlock?: (key: string) => void
  onOverride: (frameworkKey: string) => void
  onClearOverride: (frameworkKey: string) => void
  onAddScip: (productId: string, productNumber: string | null) => void
  overrideByName?: (userId: string | null) => string | null
}) {
  return (
    <div className="flex flex-col gap-2">
      {frameworks.map((f) => {
        const isOpen = expanded[f.frameworkKey] ?? true
        return (
          <FrameworkResultRow
            key={f.frameworkKey}
            framework={f}
            expanded={isOpen}
            readOnly={readOnly}
            canOverride={canOverride}
            canEditProducts={canEditProducts}
            onToggleExpand={() => onToggleExpand(f.frameworkKey, !isOpen)}
            onToggleInclude={() => onToggleInclude(f.frameworkKey, !f.selected)}
            onHover={onHover}
            onJumpToBlock={
              onJumpToBlock ? () => onJumpToBlock(f.frameworkKey) : undefined
            }
            onOverride={() => onOverride(f.frameworkKey)}
            onClearOverride={() => onClearOverride(f.frameworkKey)}
            onAddScip={onAddScip}
            overrideByName={overrideByName?.(f.overrideBy) ?? null}
          />
        )
      })}
    </div>
  )
}
